import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useModal, useTrackerBlocker } from "src/hooks";
import { useAppDispatch } from "src/store";
import { MenuOption } from "src/components/MenuDropdown/types";
import {
  updateDashboard,
  fetchWidgets,
  updateSearch,
  updateSearchConfiguration,
  updateTrackersCollections,
} from "src/store/actions";
import { ROUTES, DASHBOARD_VISIBILITY_OPTIONS } from "src/constants";
import { isWidgetIdTypeGuard, showToastNotification } from "src/utils";
import { getDashboardDefaultLayoutSettings } from "src/store/dashboards/utils";
import {
  CreateEventModal,
  ShareDashboardModal,
  AddTrackerToCompare,
  RenameDashboardModal,
  DashboardEventsSidebar,
  DuplicateDashboardModal,
  ConfirmDashboardDeletionModal,
  ConfirmRecalculateTopicsModal,
  TrackerKeywordsDataSourcesIcon,
} from "src/features";
import {
  selectUser,
  selectWhiteLabel,
  selectWidgetsInfo,
  selectDashboardById,
  selectUserPermissions,
  selectIsCompanyDashboard,
  selectTrackersCollectionTrackers,
  selectAvailableWidgetIdsByCompanyId,
} from "src/store/selectors";

type Props = {
  closeSidebar: () => void;
  setSidebarContent: (content: JSX.Element) => void;
  trackersCollectionId: TrackersCollection.Data["id"];
  dashboardDateRangeId: DashboardDateRange.Data["id"];
  setExpandedWidgetId: (value: Widget.IdType | null) => void;
};

export const useDashboardSettingsOptions = ({
  closeSidebar,
  setSidebarContent,
  setExpandedWidgetId,
  trackersCollectionId,
  dashboardDateRangeId,
}: Props): MenuOption[] => {
  const { t } = useTranslation();

  const history = useHistory();

  const dispatch = useAppDispatch();

  const { setModal, closeModal } = useModal();

  const { hasTrackerAccess, showSubscriptionPlanLimitNotification } =
    useTrackerBlocker();

  const dashboard = useSelector((state: Store.RootState) =>
    selectDashboardById(state, trackersCollectionId),
  );

  const dashboardCompanyId = useMemo<Company.Data["id"]>(
    () => dashboard?.companyId || "",
    [dashboard?.companyId],
  );

  const { isUserCompanyOwnerOrAdmin } = useSelector(selectUserPermissions);

  const trackers = useSelector((state: Store.RootState) =>
    selectTrackersCollectionTrackers(state, trackersCollectionId),
  );

  const user = useSelector(selectUser);

  const widgetsInfo = useSelector(selectWidgetsInfo);

  const isCompanyDashboard = useSelector((state: Store.RootState) =>
    selectIsCompanyDashboard(state, trackersCollectionId),
  );

  const availableWidgetIds = useSelector((state: Store.RootState) =>
    selectAvailableWidgetIdsByCompanyId(state, dashboardCompanyId),
  );

  const { defaultDashboard: whiteLabelDefaultDashboard } =
    useSelector(selectWhiteLabel);

  const isUserDashboardOwner = useMemo<boolean>(
    () => user.id === dashboard?.authorId,
    [dashboard?.authorId, user.id],
  );

  const isDuplicateDashboardShown = useMemo<boolean>(
    () => isCompanyDashboard,
    [isCompanyDashboard],
  );

  const isLockDashboardShown = useMemo<boolean>(
    () => isUserDashboardOwner || isUserCompanyOwnerOrAdmin,
    [isUserCompanyOwnerOrAdmin, isUserDashboardOwner],
  );

  const onRenameTrackersViewClickHandler = useCallback(
    (): void =>
      setModal(
        "rename-dashboard",
        <RenameDashboardModal dashboardId={trackersCollectionId} />,
      ),
    [trackersCollectionId, setModal],
  );

  const onResetDashboardLayout = useCallback(async (): Promise<void> => {
    const { layouts, tiles } = getDashboardDefaultLayoutSettings({
      widgetsInfo,
      availableWidgetIds: [...availableWidgetIds],
      whiteLabelDefaultDashboard,
    });

    const widgetIdsToFetch: Widget.IdType[] = [];

    for (const widgetId in tiles) {
      if (!isWidgetIdTypeGuard(widgetId)) continue;

      const isWidgetAdded = Boolean(dashboard?.tiles[widgetId]);

      if (!isWidgetAdded) widgetIdsToFetch.push(widgetId);
    }

    try {
      await dispatch(
        updateDashboard({
          id: trackersCollectionId,
          changes: { tiles, layouts },
        }),
      ).unwrap();

      await dispatch(
        fetchWidgets({
          dashboardDateRangeId,
          trackersCollectionId,
          widgetIds: widgetIdsToFetch,
        }),
      ).unwrap();

      setExpandedWidgetId(null);
    } catch (error) {
      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });

      console.error(error);
    }
  }, [
    t,
    dispatch,
    widgetsInfo,
    dashboard?.tiles,
    availableWidgetIds,
    setExpandedWidgetId,
    trackersCollectionId,
    dashboardDateRangeId,
    whiteLabelDefaultDashboard,
  ]);

  const executeRecalculateTopics = useCallback(async (): Promise<void> => {
    const promises: Promise<unknown>[] = [];

    for (const tracker of trackers) {
      for (const searchId of tracker.searchIds) {
        promises.push(
          dispatch(
            updateSearch({
              id: searchId,
              changes: {
                effectiveFrom: null,
                changeType: null,
              },
            }),
          ).unwrap(),
        );

        promises.push(
          dispatch(
            updateSearchConfiguration({
              id: searchId,
              changes: {
                keywordsMetadata: [],
              },
            }),
          ).unwrap(),
        );
      }
    }

    promises.push(
      dispatch(
        updateTrackersCollections([
          {
            id: trackersCollectionId,
            changes: { updatedAt: new Date().toISOString() },
          },
        ]),
      ).unwrap(),
    );

    await Promise.all(promises);
  }, [dispatch, trackersCollectionId, trackers]);

  const onRecalculateTopics = useCallback((): void => {
    setModal(
      "confirm-recalculate-topics",
      <ConfirmRecalculateTopicsModal
        onConfirm={executeRecalculateTopics}
        onCancel={() => closeModal("confirm-recalculate-topics")}
      />,
    );
  }, [setModal, closeModal, executeRecalculateTopics]);

  const onDuplicateDashboardClickHandler = useCallback(
    (): void =>
      setModal(
        "duplicate-dashboard",
        <DuplicateDashboardModal trackersCollectionId={trackersCollectionId} />,
      ),
    [setModal, trackersCollectionId],
  );

  const onDeleteTrackersViewClickHandler = useCallback(
    (): void =>
      setModal(
        "confirm-dashboard-deletion",
        <ConfirmDashboardDeletionModal
          trackersCollectionId={trackersCollectionId}
        />,
      ),
    [setModal, trackersCollectionId],
  );

  const onEditTrackerClick = useCallback(
    ({ id }: Tracker.Data): void => {
      if (!hasTrackerAccess) return showSubscriptionPlanLimitNotification();

      return history.push(`${ROUTES.trackersPage}/edit/${id}`, {
        redirectUrl: history.location.pathname,
      });
    },
    [history, hasTrackerAccess, showSubscriptionPlanLimitNotification],
  );

  const onToggleIsDashboardLocked = useCallback(async (): Promise<void> => {
    const updatedIsLocked = !dashboard?.isLocked;

    try {
      await dispatch(
        updateDashboard({
          id: trackersCollectionId,
          changes: { isLocked: updatedIsLocked },
        }),
      ).unwrap();

      showToastNotification({
        type: "success",
        text: updatedIsLocked
          ? t("page.dashboard.status.success.dashboard_locked")
          : t("page.dashboard.status.success.dashboard_unlocked"),
      });
    } catch (error) {
      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });

      console.error(error);
    }
  }, [dashboard?.isLocked, dispatch, t, trackersCollectionId]);

  const onChangeVisibilityClick = useCallback(
    async (visibility: Dashboard.Visibility): Promise<void> => {
      if (!isUserDashboardOwner) return;

      try {
        await dispatch(
          updateDashboard({
            id: trackersCollectionId,
            changes: { visibility },
          }),
        ).unwrap();

        showToastNotification({
          type: "success",
          text: t("page.dashboard.status.success.dashboard_visibility_updated"),
        });
      } catch (error) {
        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });

        console.error(error);
      }
    },
    [isUserDashboardOwner, trackersCollectionId, dispatch, t],
  );

  const onAddTrackerSubmit = useCallback((): void => {
    closeSidebar();

    closeModal("add-tracker");
  }, [closeModal, closeSidebar]);

  const onAddToCompareButtonClick = useCallback((): void => {
    if (!isCompanyDashboard) return;

    setSidebarContent(
      <AddTrackerToCompare
        submitHandler={onAddTrackerSubmit}
        trackersCollectionId={trackersCollectionId}
      />,
    );
  }, [
    setSidebarContent,
    isCompanyDashboard,
    onAddTrackerSubmit,
    trackersCollectionId,
  ]);

  const onShareDashboardClick = useCallback(
    (): void =>
      setModal(
        "share-dashboard",
        <ShareDashboardModal
          dashboardId={trackersCollectionId}
          dashboardDateRangeId={dashboardDateRangeId}
        />,
      ),
    [dashboardDateRangeId, trackersCollectionId, setModal],
  );

  const editTrackerOptions = useMemo<MenuOption[]>(
    () =>
      trackers.map((tracker) => ({
        label: tracker.name,
        onClick: () => onEditTrackerClick(tracker),
        icon: <TrackerKeywordsDataSourcesIcon trackerId={tracker.id} />,
      })),
    [onEditTrackerClick, trackers],
  );

  const changeVisibilityOptions = useMemo<MenuOption[]>(() => {
    const options: MenuOption[] = [];

    for (const option of DASHBOARD_VISIBILITY_OPTIONS) {
      const { value, label, checkIsAvailable } = option;

      if (value === dashboard?.visibility) continue;

      if (checkIsAvailable && !checkIsAvailable(user)) continue;

      options.push({
        label: t(label),
        onClick: () => onChangeVisibilityClick(value),
      });
    }

    return options;
  }, [dashboard?.visibility, onChangeVisibilityClick, t, user]);

  const eventsOptions = useMemo<MenuOption[]>(() => {
    const options: MenuOption[] = [];

    options.push(
      {
        label: t("page.dashboard.button.create_event"),
        onClick: () =>
          setModal(
            "create-event",
            <CreateEventModal dashboardId={trackersCollectionId} />,
          ),
      },
      {
        label: t("page.dashboard.button.open_edit_events"),
        onClick: () =>
          setSidebarContent(
            <DashboardEventsSidebar dashboardId={trackersCollectionId} />,
          ),
      },
    );

    return options;
  }, [t, setModal, trackersCollectionId, setSidebarContent]);

  return useMemo<MenuOption[]>(() => {
    const options: MenuOption[] = [
      {
        group: 1,
        icon: "PencilOutline",
        responsivenessType: "mobile",
        options: editTrackerOptions,
        label: t("page.dashboard.button.edit_tracker"),
      },
      {
        group: 1,
        icon: "Compare",
        responsivenessType: "mobile",
        onClick: onAddToCompareButtonClick,
        label: t("page.dashboard.button.compare_tracker"),
      },
      {
        group: 2,
        icon: "TimerOutline",
        label: t("page.dashboard.label.events"),
        options: eventsOptions,
      },
      {
        group: 3,
        icon: "Rename",
        label: t("page.dashboard.button.rename_dashboard"),
        onClick: onRenameTrackersViewClickHandler,
      },
    ];

    if (isDuplicateDashboardShown)
      options.push({
        group: 3,
        icon: "DuplicateOutline",
        label: t("page.dashboard.button.duplicate_dashboard"),
        onClick: onDuplicateDashboardClickHandler,
      });

    if (isUserDashboardOwner)
      options.push({
        group: 3,
        icon: "EyeOutline",
        label: t("page.dashboard.button.update_dashboard_visibility"),
        options: changeVisibilityOptions,
      });

    if (isLockDashboardShown && !dashboard?.isLocked)
      options.push({
        group: 3,
        icon: "LockOutline",
        label: t("page.dashboard.button.lock_dashboard"),
        onClick: onToggleIsDashboardLocked,
      });

    if (isLockDashboardShown && dashboard?.isLocked)
      options.push({
        group: 3,
        icon: "UnlockOutline",
        label: t("page.dashboard.button.unlock_dashboard"),
        onClick: onToggleIsDashboardLocked,
      });

    options.push(
      {
        group: 3,
        icon: "LinkOutline",
        responsivenessType: "mobile",
        onClick: onShareDashboardClick,
        label: t("page.dashboard.button.share_dashboard"),
      },
      {
        group: 3,
        icon: "Reset",
        label: t("page.dashboard.button.reset_layout"),
        onClick: onResetDashboardLayout,
      },
      {
        group: 3,
        icon: "Sync",
        label: t("page.dashboard.button.recalculate_topics"),
        onClick: onRecalculateTopics,
      },
      {
        group: 4,
        icon: "TrashOutline",
        label: t("page.dashboard.button.delete_dashboard"),
        onClick: onDeleteTrackersViewClickHandler,
        type: "danger",
      },
    );

    return options;
  }, [
    t,
    eventsOptions,
    editTrackerOptions,
    dashboard?.isLocked,
    isUserDashboardOwner,
    isLockDashboardShown,
    onShareDashboardClick,
    onResetDashboardLayout,
    onRecalculateTopics,
    changeVisibilityOptions,
    onAddToCompareButtonClick,
    onToggleIsDashboardLocked,
    isDuplicateDashboardShown,
    onRenameTrackersViewClickHandler,
    onDeleteTrackersViewClickHandler,
    onDuplicateDashboardClickHandler,
  ]);
};
